// Taşeron portalı — auth-gated uygulama düzeni (S37-1, Carbon 2a UI Shell): 48px koyu header
// (firma switcher dahil, hiç kaymaz) + 256px SideNav + `<main>` (sayfanın TEK kaydırıcısı,
// `h-dvh`) + Belge Analizi durum çubuğu (tray). Mekanik web-expert `AppLayout`'tan kopya
// (app sınırı: import değil): atla-bağlantısı, route değişiminde odak+scroll sıfırlama
// (ilk yüklemede DEĞİL — `prevPathnameRef`). Daraltılabilir rail (kullanıcı başına kalıcı, `uiPrefs.ts`); bölüm başlığı yok (5 öğe). Tüm iş ekranları supplier'ın **kendi** taşeron
// firmasına kapsanır (api-contract §13.6).
//
// `DocumentAnalysisProvider` burada BİR KEZ monte edilir (S17-D11) — rota değişse de
// yükleme kuyruğu/aktif analiz kaybolmaz. `DocumentAnalysisTray`, `<main>`'in KARDEŞİDİR
// (`Outlet` dışında) — böylece rota değişiminde KALAN tek yüzeydir.

import { SideNav, type SideNavItem, cn } from '@isgportal/shared';
import { ClipboardCheck, FileText, LayoutDashboard, Smartphone, Truck, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { DocumentAnalysisTray } from '../components/DocumentAnalysisTray';
import { DocumentAnalysisProvider } from '../lib/DocumentAnalysisProvider';
import { useSupplierContractorAccess } from '../lib/supplierContractorAccess';
import { useNavRailCollapsed } from '../lib/uiPrefs';

export function AppLayout() {
  const { t } = useTranslation();
  // Personel yönetimi + Çalışma İzinleri menüleri yalnız `supplier-contractor` (Yetkili)
  // sondası geçerse görünür (§13.6); yalnız `supplier-self` (Personeli) principal onları GÖRMEZ.
  // Asıl yetki backend'de — bu yalnız UX görünürlüğüdür. "Mobil Uygulama" (§26) panel-gate
  // DIŞI, herhangi authenticated okur; en sonda durur (S37 C4: eski sıradan farklı).
  const supplierContractor = useSupplierContractorAccess();

  const [railCollapsed, setRailCollapsed] = useNavRailCollapsed();

  const items: SideNavItem[] = [
    { label: t('layout.dashboard'), to: '/', icon: LayoutDashboard },
    {
      label: t('layout.documents'),
      to: '/documents',
      icon: FileText,
      matchPrefixes: ['/document-analysis'],
    },
    ...(supplierContractor.status === 'granted'
      ? [
          {
            label: t('layout.personnel'),
            to: '/personnel',
            icon: Users,
            matchPrefixes: ['/personnel'],
          },
          {
            label: t('layout.vehicles'),
            to: '/vehicles',
            icon: Truck,
            matchPrefixes: ['/vehicles'],
          },
          {
            label: t('layout.permits'),
            to: '/permits',
            icon: ClipboardCheck,
            matchPrefixes: ['/permits'],
          },
        ]
      : []),
    { label: t('layout.mobileApp'), to: '/mobile-app', icon: Smartphone },
  ];

  // Route değişiminde odak + scroll `<main>`e taşınır (SPA gezinmesi sayfa yüklemesi değil);
  // ilk yüklemede DEĞİL — aksi hâlde sayfa açılışında odak çalınır (S36 #9b).
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const [trayHeight, setTrayHeight] = useState(0);
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current === location.pathname) {
      return;
    }
    prevPathnameRef.current = location.pathname;
    mainRef.current?.focus();
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  return (
    <DocumentAnalysisProvider>
      <div className="flex h-dvh flex-col bg-bg">
        {/* WCAG 2.4.1 atla-bağlantısı — salt yerel çapa (hedef `tabIndex={-1}` taşır). */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[var(--z-toast)] focus:rounded-md focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-fg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {t('layout.skipToContent')}
        </a>
        <AppHeader />
        <div className="flex min-h-0 flex-1">
          <aside
            className={cn(
              'hidden shrink-0 flex-col border-r border-border bg-surface transition-[width]',
              'duration-100 ease-[cubic-bezier(0.2,0,0.38,0.9)] motion-reduce:transition-none md:flex',
              railCollapsed ? 'w-12' : 'w-64',
            )}
          >
            <SideNav
              items={items}
              ariaLabel={t('layout.nav.primary')}
              collapsed={railCollapsed}
              onToggleCollapsed={() => setRailCollapsed(!railCollapsed)}
              collapseLabel={t('layout.rail.collapse')}
              expandLabel={t('layout.rail.expand')}
              className="h-full"
            />
          </aside>

          <main
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            style={trayHeight > 0 ? { paddingBottom: `calc(2rem + ${trayHeight}px)` } : undefined}
            className="min-w-0 flex-1 overflow-y-auto px-8 pt-6 pb-8 outline-none"
          >
            <Outlet />
          </main>
          {/* Tray `<main>`in KARDEŞİ (Outlet dışı) — rota değişiminde KALAN tek yüzey. */}
          <DocumentAnalysisTray onHeightChange={setTrayHeight} />
        </div>
      </div>
    </DocumentAnalysisProvider>
  );
}
